import { useMemo } from 'react';
import { useRepositoryTypes, type Repository } from '../api/code';
import { useGetTenantIntegrations } from '../components/onboarding/apis/tenant-integrations';
import { useListTasks } from '../api/tasks';
import { DEFAULT_TASK_TYPE } from '../pages/tasks/utils';

/**
 * Hook to determine if the user is eligible for the Code Fix task for GitHub and Node.js.
 *
 * Eligibility requires:
 * 1. GitHub integration exists and is enabled
 * 2. Node.js repositories are present
 * 3. No existing CODE_FIX task
 *
 * @returns Object with isEligible, isLoading, error states, and node repositories
 */
export const useEligibleForCodeFixTaskForGithubAndNode = (): {
  isEligible: boolean;
  isLoading: boolean;
  error: Error | null;
  nodeRepositories: Repository[];
} => {
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    error: integrationsError,
  } = useGetTenantIntegrations();

  // Extract integration IDs from enabled GitHub integrations
  const integrationIds = useMemo(() => {
    if (!integrations) {
      return [];
    }
    return integrations
      .filter(
        (integration) =>
          integration.provider?.toLowerCase() === 'github' &&
          integration.enabled,
      )
      .map((integration) => integration.integrationId)
      .filter((id): id is string => !!id);
  }, [integrations]);

  const {
    data: repositoryTypesData,
    isLoading: isLoadingRepositoryTypes,
    error: repositoryTypesError,
  } = useRepositoryTypes({
    integrationIds: integrationIds.length > 0 ? integrationIds : undefined,
    enabled: integrationIds.length > 0,
  });

  const {
    data: tasks,
    isLoading: isLoadingTasks,
    error: tasksError,
  } = useListTasks(
    { scope: DEFAULT_TASK_TYPE },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  const isLoading =
    isLoadingRepositoryTypes || isLoadingIntegrations || isLoadingTasks;

  const error = useMemo(() => {
    const firstError = repositoryTypesError || integrationsError || tasksError;
    if (!firstError) {
      return null;
    }
    return firstError instanceof Error
      ? firstError
      : new Error(String(firstError));
  }, [repositoryTypesError, integrationsError, tasksError]);

  const isEligible = useMemo(() => {
    if (isLoading) {
      return false;
    }
    return (
      (integrationIds.length > 0 &&
        repositoryTypesData?.node &&
        repositoryTypesData.node.length > 0 &&
        !tasks?.some((task) => task.taskType === 'CODE_FIX')) ??
      false
    );
  }, [isLoading, integrationIds.length, repositoryTypesData?.node, tasks]);

  const nodeRepositories = useMemo(() => {
    return repositoryTypesData?.node || [];
  }, [repositoryTypesData?.node]);

  return {
    isEligible,
    isLoading,
    error,
    nodeRepositories,
  };
};
